import { CoralogixRum } from '@coralogix/browser'
import mixpanel from 'mixpanel-browser'
import { usePostHog } from 'posthog-js/react'
import { useCallback } from 'react'

import { useOptionalOrgDetails } from '@/lib/auth/orgid.hook'

export type EventName =
  | 'third-party.add'
  | 'third-party.view'
  | 'third-party.solution.view'
  | 'third-party.profile.view'
  | 'third-party.risk.update'
  | 'third-party.status.update'
  | 'third-party.export'
  | 'alert.view'
  | 'alert.export'
  | 'signal.evidence.view'
  | 'signal.mitigation.copy'
  | 'alert.severity.update'
  | 'alert.status.update'
  | 'alert.row-selected'
  | 'alert.all-selected'
  | 'alert.all-deselected'
  | 'alert.selection-cleared'
  | 'alert.bulk-update-clicked'
  | 'alert.status-selected'
  | 'alert.severity-selected'
  | 'alert.confirmed'
  | 'alert.completed'
  | 'alert.failed'
  | 'alert-rule.add.click'
  | 'alert-rule.created'
  | 'alert-rule.updated'
  | 'alert-rule.enabled'
  | 'alert-rule.disabled'
  | 'alert-rule.deleted'
  | 'signal.false-positive.mark'
  | 'control.view'
  | 'control.view.activity.group-controls-click'
  | 'control.view.activity.source-clicked'
  | 'control.validation.update'
  | 'assessment.comment.added'
  | 'assessment.comment.edited'
  | 'assessment.comment.deleted'
  | 'assessment.comment.reaction.added'
  | 'assessment.comment.reaction.removed'
  | 'assessment.comment.mention.used'
  | 'assessment.comment.panel.opened'
  | 'assessment.comment.replied'
  | 'assessment.comment.thread.expanded'
  | 'assessment.comment.link.copied'
  | 'assessment.comment.search.used'
  | 'assessment.comment.filter.changed'
  | 'artifact.upload'
  | 'artifact.view'
  | 'artifact.error-tooltip.open'
  | 'third-party.add.manual'
  | 'third-party.add.finder'
  | 'third-party.domain.click'
  | 'third-party.irq.submit'
  | 'third-party.irq.set'
  | 'questionnaire.new'
  | 'questionnaire.finalize'
  | 'questionnaire.artifact.view'
  | 'third-party.assessment.mark-as-assessed'
  | 'third-party.assessment.past-assessment.click'
  | 'assessment.export'
  | 'assessment.export.open'
  | 'third-party.assessment.finish'
  | 'third-party.assessment.start'
  | 'business-owner.assign'
  | 'business-owner.remove'
  | 'business-owner.send-email'
  | 'business-owner.copy-email'
  | 'third-party.irq.suggestions.hover'
  | 'third-party.irq.suggestions.click'
  | 'third-party.irq.modal.open'
  | 'third-party.irq.overview.start'
  | 'third-party.irq.overview.skip-to-assign'
  | 'third-party.irq.overview.source.toggle'
  | 'third-party.irq.sidebar.navigate'
  | 'third-party.irq.question.skip-to-assign'
  | 'third-party.irq.assign.risk-change'
  | 'third-party.irq.finish.click'
  | 'third-party.irq.estimated-score.change'
  | 'questionnaire.invite.send'
  | 'questionnaire.request.artifact.uploaded'
  | 'questionnaire.recipient.view'
  | 'additional-facts'
  | 'settings.control.scope.update'
  | 'settings.control.weight.update'
  | 'settings.control.create.started'
  | 'settings.control.create.guardrail-failed'
  | 'settings.control.create.generated'
  | 'settings.control.create.completed'
  | 'settings.control.create.abandoned'
  | 'settings.control.edit.completed'
  | 'settings.control.deleted'
  | 'settings.control.bulk-action.applied'
  | 'user.role.change'
  | 'project.create'
  | 'project.delete'
  | 'project.update'
  | 'questionnaire.template.create.template'
  | 'outreach-campaign.create'
  | 'outreach-campaign.remind'
  | 'outreach-campaign.close'
  | 'outreach-campaign.delete'
  | 'outreach-campaign.add-recipients'
  | 'outreach-campaign.edit-recipients'
  | 'questionnaire.template.add.section'
  | 'questionnaire.template.add.question'
  | 'questionnaire.template.delete.section'
  | 'questionnaire.template.delete.question'
  | 'questionnaire.template.v2.add.question'
  | 'custom-field.create'
  | 'custom-field.delete'
  | 'custom-field.rename'
  | 'custom-field.option.create'
  | 'custom-field.option.delete'
  | 'third-party.custom-field.added'
  | 'third-party.custom-field.removed'
  | 'third-party.engagement-mode.enable'
  | 'third-party.engagement-mode.disable'
  | 'third-party.engagement.added'
  | 'third-party.engagement.deleted'
  | 'control.suggested-actions.copy'
  | 'third-party.smart-evidence-request.unpublish'
  | 'third-party.smart-evidence-request.publish'
  | 'third-party.smart-evidence-request.due-date.click'
  | 'third-party.smart-evidence-request.due-date.set'
  | 'third-party.questionnaire.submit'
  | 'third-party.smart-evidence-request.preview'
  | 'third-party.smart-evidence-request.go-to-third-party-portal'
  | 'risk-insight.delete'
  | 'third-party-inventory.filter.apply'
  | 'third-party-inventory.group-affiliated.toggle'
  | 'alert-inventory.filter.apply'
  | 'assessment.filter.apply'
  | 'requests.filter.apply'
  | 'generic.filter.apply'
  | 'unsanctioned.view'
  | 'unsanctioned.dismissed.view'
  | 'unsanctioned.entity.view'
  | 'unsanctioned.filter.apply'
  | 'unsanctioned.resolution.start'
  | 'unsanctioned.resolution.step.complete'
  | 'unsanctioned.resolution.complete'
  | 'unsanctioned.resolution.abandon'
  | 'unsanctioned.entity.dismiss'
  | 'unsanctioned.entity.restore'
  | 'integration.added'
  | 'integration.updated'
  | 'mcp.view'
  | 'mcp.add.click'
  | 'jira.issue-created'
  | 'third-party.reassessment-due-date.manual-set'
  | 'notification.scope.update'
  | 'third-party.activity-log.view'
  | 'third-party.bookmark.add'
  | 'third-party.bookmark.edit'
  | 'third-party.bookmark.remove'
  | 'third-party.bookmark.click'
  | 'dashboard.view'
  | 'dashboard.add.click'
  | 'dashboard.agent.responded'
  | 'dashboard.agent.failed'
  | 'dashboard.created'
  | 'dashboard.updated'
  | 'dashboard.saved'
  | 'dashboard.changes.discarded'
  | 'dashboard.duplicated'
  | 'dashboard.deleted'
  | 'dashboard.export'
  | 'dashboard-list.filter.apply'
  | 'dashboard.filter.apply'
  | 'dashboard.refresh.click'
  | 'third-party-inventory.view'
  | 'alert-inventory.view'
  | 'assessment-list.view'
  | 'requests.view'
  | 'outreach.view'
  | 'past-assessment.view'
  | 'company-request.view'
  | 'third-party.tab.view'
  | 'settings.view'
  | 'settings.section.view'
  | 'settings.notifications.tab.view'
  | 'settings.notifications.business-owner-cc.toggle'
  | 'settings.control-studio.tab.view'
  | 'third-party.add.click'
  | 'third-party.assessment.start.click'
  | 'third-party.assessment.view'
  | 'third-party.assessment.top.navigation.click'
  | 'third-party.assessment.tab.view'
  | 'third-party.assessment.overview.issues.click'
  | 'third-party.assessment.overview.tab-shortcut.click'
  | 'third-party.assessment.controls.filter.apply'
  | 'third-party.assessment.controls.group-by-scope.toggle'
  | 'third-party.assessment.controls.scope.click'
  | 'third-party.assessment.issues.search'
  | 'third-party.assessment.issues.filter.apply'
  | 'third-party.assessment.conclusion.edit'
  | 'third-party.assessment.conclusion.format.apply'
  | 'third-party.assessment.jira-ticket.create.click'
  | 'third-party.assessment.assignee.add.click'
  | 'third-party.assessment.company-panel.toggle'
  | 'third-party.assessment.company-info.view-more.click'
  | 'questionnaire.new.click'
  | 'questionnaire.invite.click'
  | 'signal.add.click'
  | 'custom-field.create.click'
  | 'project.create.click'
  | 'user.invite.click'
  | 'artifact.delete'
  | 'third-party.assessment.delete'
  | 'questionnaire.delete'
  | 'questionnaire.recipients.remove'
  | 'user.invited'
  | 'user.removed'
  | 'integration.removed'
  | 'portal.login'
  | 'portal.logout'
  | 'portal.landing-page.view'
  | 'portal.questionnaire.view'
  | 'portal.all-requests.switch'
  | 'portal.review-tab.switch'
  | 'portal.section.toggle'
  | 'portal.artifact.upload.start'
  | 'portal.artifact.upload.complete'
  | 'portal.artifact.upload.failed'
  | 'portal.artifact.analyze.click'
  | 'portal.answer.selected'
  | 'portal.comment.submitted'
  | 'portal.clarification.completed'
  | 'portal.invite.click'
  | 'portal.invite.sent'
  | 'portal.top.navigation.click'
  | 'assessment.controls.group-by.toggle'
  | 'assessment.controls.control-selected'
  | 'assessment.controls.all-selected'
  | 'assessment.controls.all-deselected'
  | 'assessment.controls.selection-cleared'
  | 'assessment.controls.bulk-update-clicked'
  | 'assessment.controls.status-selected'
  | 'assessment.controls.confirmed'
  | 'assessment.controls.completed'
  | 'assessment.controls.failed'
  | 'sidebar_item_clicked'
  | 'overview_item_clicked'
  | 'hyperlink_clicked'
  | 'ai-governance.widget.click'

export const useTrackCallback = (eventName: EventName) => {
  const posthog = usePostHog()
  const { orgDetails } = useOptionalOrgDetails()

  const callback = useCallback(
    async (properties?: Record<string, unknown>) => {
      if (!posthog) {
        return
      }
      posthog.capture(eventName, properties)
      CoralogixRum.info(eventName, properties)

      // Mixpanel group tracking
      if (orgDetails) {
        // must use set_group and not add_group
        // otherwise when switching tenants in single session, event will be attributed to both organizations
        // https://docs.mixpanel.com/docs/tracking-methods/sdks/javascript#group-analytics
        mixpanel.set_group('organization', orgDetails.orgId)
        properties = {
          ...properties,
          organization_name: orgDetails.orgName,
        }
      }
      mixpanel.track(eventName, properties)
    },
    [posthog, eventName, orgDetails],
  )

  return callback
}

export type EventProperties = Record<
  string,
  string | boolean | number | undefined | null | string[]
>
